import { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as SyncedIcon } from "assets/svgs/sync/Synced.svg";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT } from "lib/utils/dateTimeDisplay";
import ExpandableCard from "components/layout/motion/ExpandableCard";

export interface MissionListItem {
  key: string;
  sourceArcSerial?: string;
  sourceMissionId: string;
  name: string;
  created_at: string;
  action_count: number;
}

type MissionSortMode = "createdAt" | "name";

interface MissionListViewProps {
  missions: MissionListItem[];
  onSelect: (mission: MissionListItem) => void;
  isLoading?: boolean;
  isError?: boolean;
  sortMode?: MissionSortMode;
  searchQuery?: string;
  activeMissionId?: string | null;
  isMissionActive?: boolean;
  readOnly?: boolean;
  isEditMode?: boolean;
  selectedIds?: string[];
  onToggleSelection?: (id: string) => void;
  onEnterEditMode?: () => void;
  isDeleting?: boolean;
  onEdit?: (missionId: string) => void;
  scrollToMissionId?: string | null;
  scrollWhenVisible?: boolean;
}

const MissionListView: React.FC<MissionListViewProps> = ({ 
  missions,
  onSelect,
  isLoading = false,
  isError = false,
  sortMode = "createdAt",
  searchQuery = "",
  activeMissionId = null,
  isMissionActive = false,
  readOnly = false,
  isEditMode = false,
  selectedIds = [],
  onToggleSelection = () => {},
  onEnterEditMode = () => {},
  isDeleting = false,
  onEdit,
  scrollToMissionId = null,
  scrollWhenVisible = true,
}) => {
  const { t } = useTranslation(["missions", "common"]);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const missionRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const lastScrolledMissionIdRef = useRef<string | null>(null);

  const orderedMissions = useMemo(() => {
    return [...missions].sort((a, b) => {
      if (sortMode === "name") {
        return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
      }
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return bTime - aTime;
    });
  }, [missions, sortMode]);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredMissions = useMemo(() => {
    if (!normalizedSearchQuery) return orderedMissions;
    return orderedMissions.filter((mission) =>
      mission.name.toLowerCase().includes(normalizedSearchQuery)
    );
  }, [orderedMissions, normalizedSearchQuery]);

  // One-shot scroll: re-running on every missions array identity change (parent
  // re-renders during mission execution) was yanking the list back to the target.
  useEffect(() => {
    if (!scrollToMissionId || !scrollWhenVisible) return;
    if (!filteredMissions.some((mission) => mission.key === scrollToMissionId)) return;
    if (lastScrolledMissionIdRef.current === scrollToMissionId) return;

    lastScrolledMissionIdRef.current = scrollToMissionId;
    const frame = requestAnimationFrame(() => {
      missionRowRefs.current.get(scrollToMissionId)?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });

    return () => cancelAnimationFrame(frame);
  }, [scrollToMissionId, filteredMissions, scrollWhenVisible]);

  useEffect(() => {
    if (scrollWhenVisible) return;
    // Allow one intentional scroll again when the list is shown next time.
    lastScrolledMissionIdRef.current = null;
  }, [scrollWhenVisible]);

  useEffect(() => {
    if (scrollToMissionId) return;
    lastScrolledMissionIdRef.current = null;
  }, [scrollToMissionId]);

  const setMissionRowRef = (missionId: string) => (element: HTMLDivElement | null) => {
    if (element) {
      missionRowRefs.current.set(missionId, element);
    } else {
      missionRowRefs.current.delete(missionId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-h4 text-black60">{t("list.loading")}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-h4 text-red-500">{t("list.error")}</span>
      </div>
    );
  }

  const handleTouchStart = () => {
    if (!readOnly && !isEditMode) {
      timerRef.current = setTimeout(() => {
        onEnterEditMode();
      }, 800);
    }
  };

  const handleTouchEnd = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!readOnly && !isEditMode) {
      onEnterEditMode();
    }
  };

  const handleClick = (mission: MissionListItem) => {
    if (!readOnly && isEditMode) {
      onToggleSelection(mission.key);
    } else {
      onSelect(mission);
    }
  };

  return (
    <div className="relative h-full w-full">
      {isDeleting && (
        <div className="absolute inset-0 z-50 bg-black90 bg-opacity-75 flex items-center justify-center">
          <span className="text-h4 font-bold text-white">{t("list.deleting")}</span>
        </div>
      )}

      {missions.length === 0 ? (
        <div className="flex h-full items-center justify-center">
          <span className="text-h4 text-black60">{t("list.empty")}</span>
        </div>
      ) : filteredMissions.length === 0 ? (
        <div className="flex h-full items-center justify-center">
          <span className="text-h4 text-black60">{t("list.noSearchResults")}</span>
        </div>
      ) : (
        <>
          {filteredMissions.map((mission) => {
            const isRunningMission = isMissionActive && activeMissionId === mission.key;
            return (
              <ExpandableCard
                key={mission.key}
                expandId={mission.key}
                className="flex w-full min-w-0 cursor-pointer items-center rounded border-b border-black80 p-2 transition-colors hover:bg-black80"
                onClick={() => handleClick(mission)}
                onContextMenu={readOnly ? undefined : handleContextMenu}
                onTouchStart={readOnly ? undefined : handleTouchStart}
                onTouchEnd={readOnly ? undefined : handleTouchEnd}
                onTouchCancel={readOnly ? undefined : handleTouchEnd}
                onTouchMove={readOnly ? undefined : handleTouchEnd}
              >
                <div ref={setMissionRowRef(mission.key)} className="flex min-w-0 w-full items-center">
                  {!readOnly && isEditMode && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(mission.key)}
                      readOnly
                      className="mr-3 h-5 w-5 shrink-0 rounded border-black60 bg-black70 accent-primary2"
                    />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col space-y-2">
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <p className="min-w-0 truncate text-h5 text-black40">
                        {formatLocalDateTime(mission.created_at, LOCAL_DATETIME_MEDIUM_SHORT)}
                      </p>
                      <SyncedIcon className="w-3 shrink-0" />
                    </div>
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <p
                        className="min-w-0 flex-1 truncate text-h5 font-semibold"
                        title={mission.name}
                      >
                        {mission.name}
                      </p>
                      <div className="flex shrink-0 items-center gap-2">
                        {isRunningMission && (
                          <span className="rounded-r5 border border-low bg-black80 px-2 py-[2px] text-h5 text-low">
                            {t("list.running")}
                          </span>
                        )}
                        <p className="text-h5 text-black60">
                          {t("list.actions", { count: mission.action_count })}
                        </p>
                      </div>
                    </div>
                    {mission.sourceArcSerial && (
                      <p className="truncate text-h5 text-black60">ARC {mission.sourceArcSerial}</p>
                    )}
                  </div>
                  {!readOnly && !isEditMode && onEdit && (
                    <button
                      className="ml-2 shrink-0 rounded bg-black70 px-3 py-1 text-h5 text-white hover:bg-black60"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(mission.key);
                      }}
                    >
                      {t("common:edit")}
                    </button>
                  )}
                </div>
              </ExpandableCard>
            );
          })}
        </>
      )}
    </div>
  );
};

export default MissionListView;
